import React, { useState, useRef, DragEvent } from "react";
import {
	UseControllerProps,
	useController,
	useFormContext,
	useWatch
} from "react-hook-form";
import { Trash2, Upload, Plus, Loader2 } from "lucide-react";
import Lightbox from "yet-another-react-lightbox";
import Download from "yet-another-react-lightbox/plugins/download";
import "yet-another-react-lightbox/styles.css";
import "src/features/SocialFeedContent/components/SocialPost/lightbox-overrides.css";
import axios from "axios";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";
import { useAzureFileUpload } from "src/components/AssetUploaders/useAzureFileUpload";
import { TextActionOptional } from "src/features/observations/components/TextActionOptional";
import assetClient from "src/services/assetClient";

const IMAGE_UPLOAD_LIMIT_COUNT = 6;
const IMAGE_PROFILE: ImageProfile = "observation-question-upload";

interface ImageUploadQuestionProps extends UseControllerProps<ObservationFormState> {
	questionNumber: number;
	images?: string[];
	staticInput?: boolean;
	showAction?: boolean;
	showUploadButton?: boolean;
}

const ImageUploadQuestion: React.FC<ImageUploadQuestionProps> = ({
	questionNumber,
	name,
	rules,
	images = [],
	showAction,
	staticInput,
	showUploadButton = true
}) => {
	const { control } = useFormContext<ObservationFormState>();
	const { isSubmittingAnswer } = useWatch({ control });
	const {
		field: { onChange }
	} = useController({ control, name, rules });

	const { upload: uploadFile } = useAzureFileUpload();
	const [selectedFilesCount, setSelectedFilesCount] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const inputRef = React.useRef<HTMLInputElement>(null);

	// Lightbox portal container — render inside the closest dialog so Radix
	// focus-trap and dismiss-layer don't block lightbox interactions.
	const lightboxPortalRef = useRef<HTMLDivElement>(null);

	// Lightbox state for static/preview mode
	const [lightboxIndex, setLightboxIndex] = useState(-1);
	const [lightboxSlides, setLightboxSlides] = useState<
		Array<{ src: string; download: { url: string; filename: string } }>
	>([]);
	const slideCachedAt = React.useRef<number>(0);
	const SLIDE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

	const openLightbox = async (startIndex: number) => {
		const cacheValid =
			Date.now() - slideCachedAt.current < SLIDE_CACHE_TTL &&
			lightboxSlides.length === images.length &&
			lightboxSlides.every(
				(s, i) => s.download.filename === `image-${images[i]}`
			);

		if (cacheValid) {
			setLightboxIndex(startIndex);
			return;
		}

		const slides = await Promise.all(
			images.map(async (uuid) => {
				try {
					const { data } = await assetClient.get(
						`/api/v1.0/asset/image/${uuid}/all`,
						{ params: { profileName: IMAGE_PROFILE } }
					);
					const url = data?.full || data?.default || "";
					return {
						src: url,
						download: { url, filename: `image-${uuid}` }
					};
				} catch {
					return { src: "", download: { url: "", filename: `image-${uuid}` } };
				}
			})
		);
		setLightboxSlides(slides);
		slideCachedAt.current = Date.now();
		setLightboxIndex(startIndex);
	};

	const handleFiles = async (selectedFiles: File[]) => {
		const validFiles = selectedFiles.filter(
			(f) => f.type === "image/jpeg" || f.type === "image/png"
		);

		if (validFiles.length === 0) {
			alert("Only JPG / PNG file types can be selected");
			return;
		}

		if (images.length + validFiles.length > IMAGE_UPLOAD_LIMIT_COUNT) {
			return alert(
				`You are only allowed to upload a maximum of ${IMAGE_UPLOAD_LIMIT_COUNT} images`
			);
		}

		setSelectedFilesCount(validFiles.length);

		try {
			const uuidList = await Promise.all(
				validFiles.flatMap((file) =>
					uploadFile(file, "images", undefined, IMAGE_PROFILE)
				)
			);
			const imageList = [...images, ...uuidList.flat()];
			onChange(imageList);
		} catch (error) {
			console.error("Error uploading files:", error);
		} finally {
			setSelectedFilesCount(0);
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files) return;
		handleFiles(Array.from(e.target.files));
	};

	const handleDragOver = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		if (!e.dataTransfer.files) return;
		handleFiles(Array.from(e.dataTransfer.files));
	};

	const handleRemoveImageClick = (id: string) => {
		if (!images) return;
		const filteredImages = images.filter((uuid) => uuid !== id);
		onChange(filteredImages);
	};

	const isUploading =
		selectedFilesCount > 0 && !isSubmittingAnswer?.includes(questionNumber);

	const isAtLimit = images?.length === IMAGE_UPLOAD_LIMIT_COUNT;
	const hasImages = images && images.length > 0;

	return (
		<div>
			<input
				ref={inputRef}
				type="file"
				accept="image/jpeg,image/png"
				multiple
				onChange={handleFileChange}
				style={{ display: "none" }}
			/>

			{/* Image grid */}
			{(hasImages || isUploading) && (
				<div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
					{images.map((uuid, i) => (
						<div
							key={uuid}
							className={[
								"rounded-lg relative bg-muted overflow-hidden h-[160px]",
								staticInput ? "cursor-pointer" : ""
							].join(" ")}
							onClick={
								staticInput
									? (e) => {
											e.stopPropagation();
											openLightbox(i);
										}
									: undefined
							}
							role={staticInput ? "button" : undefined}
							tabIndex={staticInput ? 0 : undefined}
							aria-label={
								staticInput
									? `View image ${i + 1} of ${images.length}`
									: undefined
							}
							onKeyDown={
								staticInput
									? (e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												openLightbox(i);
											}
										}
									: undefined
							}
						>
							<AzureImage
								uuid={uuid}
								imageProfile={IMAGE_PROFILE}
								linkToFull={!staticInput}
								openInModal={false}
								style={{
									objectFit: "contain",
									width: "100%",
									height: "100%"
								}}
							/>
							{!staticInput && (
								<button
									type="button"
									className="absolute top-2 right-2 bg-white rounded-lg p-1.5 shadow-sm text-destructive hover:bg-foreground hover:text-white disabled:opacity-50 transition-colors"
									title="Remove image"
									aria-label="Remove"
									onClick={() => handleRemoveImageClick(uuid)}
									disabled={isUploading}
								>
									<Trash2 className="h-4 w-4" />
								</button>
							)}
						</div>
					))}

					{isUploading &&
						Array.from({ length: selectedFilesCount }).map((_, index) => (
							<div
								key={`loading-${index}`}
								className="h-[160px] rounded-lg bg-muted flex items-center justify-center"
							>
								<Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
							</div>
						))}
				</div>
			)}

			{/* Upload area */}
			{showUploadButton && !staticInput && !isAtLimit && !isUploading && (
				<>
					{hasImages ? (
						<button
							type="button"
							onClick={() => inputRef.current?.click()}
							disabled={isUploading}
							className="inline-flex items-center gap-2 px-4 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium hover:bg-foreground hover:text-white transition-colors disabled:opacity-50"
						>
							<Plus className="h-4 w-4" />
							Add more images
						</button>
					) : (
						<div
							role="button"
							tabIndex={0}
							onClick={() => !isUploading && inputRef.current?.click()}
							onKeyDown={(e) => {
								if ((e.key === "Enter" || e.key === " ") && !isUploading) {
									e.preventDefault();
									inputRef.current?.click();
								}
							}}
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
							className={[
								"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer max-w-[480px]",
								isDragging
									? "border-primary bg-primary-lighter"
									: "border-border bg-white hover:border-primary-light hover:bg-primary-lighter/50",
								isUploading ? "pointer-events-none opacity-60" : ""
							].join(" ")}
						>
							{isUploading ? (
								<>
									<Loader2 className="h-6 w-6 text-primary animate-spin" />
									<span className="text-sm text-muted-foreground">
										Uploading...
									</span>
								</>
							) : (
								<>
									<Upload className="h-6 w-6 text-muted-foreground" />
									<div>
										<p className="text-sm font-medium text-foreground">
											Drag images here or click to browse
										</p>
										<p className="text-xs text-muted-foreground mt-1">
											JPG or PNG, up to {IMAGE_UPLOAD_LIMIT_COUNT} images
										</p>
									</div>
								</>
							)}
						</div>
					)}
				</>
			)}

			{showAction && (
				<TextActionOptional
					name={`questions.${questionNumber - 1}.action`}
					questionNumber={questionNumber}
					staticInput={staticInput}
				/>
			)}

			{staticInput && (
				<>
					<div ref={lightboxPortalRef} />
					<Lightbox
						open={lightboxIndex >= 0}
						index={lightboxIndex}
						close={() => setLightboxIndex(-1)}
						slides={lightboxSlides}
						portal={{
							root: lightboxPortalRef.current ?? undefined
						}}
						carousel={{ finite: true, padding: "15%" }}
						render={{
							buttonPrev: lightboxSlides.length <= 1 ? () => null : undefined,
							buttonNext: lightboxSlides.length <= 1 ? () => null : undefined
						}}
						on={{
							view: ({ index: i }) => setLightboxIndex(i)
						}}
						styles={{
							container: { backgroundColor: "rgba(0, 0, 0, 0.85)" },
							slide: { padding: "40px" }
						}}
						plugins={[Download]}
						download={{
							download: async ({ slide }) => {
								if (!slide.src) return;
								try {
									const response = await axios.get(slide.src, {
										responseType: "blob"
									});
									const objectUrl = URL.createObjectURL(response.data);
									const link = document.createElement("a");
									link.href = objectUrl;
									link.download =
										("download" in slide && typeof slide.download === "object"
											? slide.download?.filename
											: undefined) ?? "image";
									document.body.appendChild(link);
									link.click();
									document.body.removeChild(link);
									URL.revokeObjectURL(objectUrl);
								} catch {
									window.open(slide.src, "_blank");
								}
							}
						}}
					/>
				</>
			)}
		</div>
	);
};

export { ImageUploadQuestion, type ImageUploadQuestionProps };
